import { FunctionComponent } from "react";
import { Grid } from "@mui/material";
import { FormSection } from "../../../FormSection";
import { CustomInput } from "../../../CustomInput";
import {
  PersonOutlined,
  Person,
  LanguageOutlined,
  LocationOnOutlined,
} from "@mui/icons-material";
import {
  mdiMapMarkerRadiusOutline,
  mdiMapMarkerPath,
  mdiEmailOutline,
  mdiPhoneOutline,
} from "@mdi/js";
import { Icon } from "@mdi/react";
import { SectionProps } from "../props";
import { User } from "../../../../model/User";
import { CustomTextArea } from "../../../CustomTextArea";

type PersonalInformationState = {
  actualFirstName: string;
  actualLastName: string;
  actualAddress: string;
  actualCity: string;
  actualState: string;
  actualZip: string;
  actualPhone: string;
  phone2: string;
  actualEmail: string;
  email2: string;
  otherInfo: string;
};

export const PersonalInformation: FunctionComponent<
  SectionProps & PersonalInformationState
> = (props) => {
  const user = new User();

  const showFirstName = !user.HasForbiddenAccess("Can See First Name");
  const showLastName = !user.HasForbiddenAccess("Can See Last Name");
  const showAddress = !user.HasForbiddenAccess("Can See Address");
  const showCity = !user.HasForbiddenAccess("Can See City");
  const showState = !user.HasForbiddenAccess("Can See State");
  const showZipCode = !user.HasForbiddenAccess("Can See Zip Code");
  const showPhone1 = !user.HasForbiddenAccess("Can See Phone 1");
  const showPhone2 = !user.HasForbiddenAccess("Can See Phone 2");
  const showEmail1 = !user.HasForbiddenAccess("Can See Email 1");
  const showEmail2 = !user.HasForbiddenAccess("Can See Email 2");
  const showNotes = !user.HasForbiddenAccess("Can See Notes");

  const showPersonalInfo =
    showFirstName ||
    showLastName ||
    showAddress ||
    showCity ||
    showState ||
    showZipCode ||
    showPhone1 ||
    showPhone2 ||
    showEmail1 ||
    showEmail2 ||
    showNotes;

  const editEmailDealer = !user.HasForbiddenAccess("Can Edit Email Dealer");
  const editFirstName = !user.HasForbiddenAccess("Can Edit First Name");
  const editLastName = !user.HasForbiddenAccess("Can Edit Last Name");
  const editAddress = !user.HasForbiddenAccess("Can Edit Address");
  const editCity = !user.HasForbiddenAccess("Can Edit City");
  const editState = !user.HasForbiddenAccess("Can Edit State");
  const editZipCode = !user.HasForbiddenAccess("Can Edit Zip Code");
  const editPhone1 = !user.HasForbiddenAccess("Can Edit Phone 1");
  const editPhone2 = !user.HasForbiddenAccess("Can Edit Phone 2");
  const editEmail1 = !user.HasForbiddenAccess("Can Edit Email 1");
  const editEmail2 = !user.HasForbiddenAccess("Can Edit Email 2");
  const editNotes = !user.HasForbiddenAccess("Can Edit Notes");

  return showPersonalInfo ? (
    <FormSection title="Personal Information" isCollapsible>
      {showFirstName && (
        <Grid item>
          <CustomInput
            disabled={props.formDisabled || !editFirstName}
            value={props.actualFirstName}
            name="actualFirstname"
            startIcon={<Person />}
            label={{ text: "First Name *" }}
            height={35}
            onChange={props.onChange}
          />
        </Grid>
      )}
      {showLastName && (
        <Grid item>
          <CustomInput
            disabled={props.formDisabled || !editLastName}
            value={props.actualLastName}
            name="actualLastName"
            startIcon={<PersonOutlined />}
            label={{ text: "Last Name *" }}
            height={35}
            onChange={props.onChange}
          />
        </Grid>
      )}
      {showAddress && (
        <Grid item>
          <CustomInput
            disabled={props.formDisabled || !editAddress}
            value={props.actualAddress}
            name="actualAddress"
            startIcon={<LocationOnOutlined />}
            label={{ text: "Address *" }}
            height={35}
            onChange={props.onChange}
          />
        </Grid>
      )}
      {showCity && (
        <Grid item>
          <CustomInput
            disabled={props.formDisabled || !editCity}
            value={props.actualCity}
            name="actualCity"
            startIcon={<Icon path={mdiMapMarkerRadiusOutline} />}
            label={{ text: "City *" }}
            height={35}
            onChange={props.onChange}
          />
        </Grid>
      )}
      {showState && (
        <Grid item>
          <CustomInput
            disabled={props.formDisabled || !editState}
            value={props.actualState}
            name="actualState"
            startIcon={<LanguageOutlined />}
            label={{ text: "State *" }}
            height={35}
            onChange={props.onChange}
          />
        </Grid>
      )}
      {showZipCode && (
        <Grid item>
          <CustomInput
            disabled={props.formDisabled || !editZipCode}
            value={props.actualZip}
            name="actualZip"
            startIcon={<Icon path={mdiMapMarkerPath} />}
            label={{ text: "Zip Code *" }}
            height={35}
            onChange={props.onChange}
          />
        </Grid>
      )}
      {showPhone1 && (
        <Grid item>
          <CustomInput
            disabled={props.formDisabled || !editPhone1}
            value={props.actualPhone}
            name="actualPhone"
            startIcon={<Icon path={mdiPhoneOutline} />}
            label={{ text: "Phone 1 *" }}
            height={35}
            onChange={props.onChange}
          />
        </Grid>
      )}
      {showPhone2 && (
        <Grid item>
          <CustomInput
            disabled={props.formDisabled || !editPhone2}
            value={props.phone2}
            name="phone2"
            startIcon={<Icon path={mdiPhoneOutline} />}
            label={{ text: "Phone 2" }}
            height={35}
            onChange={props.onChange}
          />
        </Grid>
      )}
      {showEmail1 && (
        <Grid item>
          <CustomInput
            disabled={props.formDisabled || !editEmail1}
            value={props.actualEmail}
            name="actualEmail"
            startIcon={<Icon path={mdiEmailOutline} />}
            label={{ text: "Email 1 *" }}
            height={35}
            onChange={props.onChange}
          />
        </Grid>
      )}
      {showEmail2 && (
        <Grid item>
          <CustomInput
            disabled={props.formDisabled || !editEmail2}
            value={props.email2}
            name="email2"
            startIcon={<Icon path={mdiEmailOutline} />}
            label={{ text: "Email 2" }}
            height={35}
            onChange={props.onChange}
          />
        </Grid>
      )}
      <Grid item>
        <CustomTextArea
          label={{ text: "Notes" }}
          disabled={props.formDisabled || !editNotes}
          value={props.otherInfo}
          name="otherInfo"
          maxLength={500}
          onChange={props.onChange}
        />
      </Grid>
    </FormSection>
  ) : null;
};
